import type { ResourceScope } from '@kop/contracts';

export async function readDashboardManagement(scope: ResourceScope, month: string, features: readonly string[]) {
  if (scope.kind === 'tenant') throw Error('SITE_REQUIRED');
  const contractorId = scope.kind === 'contractor' ? scope.contractorId : null;
  const safe = async <T>(feature: string, read: () => Promise<T>) => features.includes(feature) ? read().catch(() => null) : null;
  const [msds, budget, meetings] = await Promise.all([
    safe('chemical-msds-management', async () => {
      const { createProductionMsdsAdapter } = await import('../chemical-msds/production-msds-adapter');
      const adapter = await createProductionMsdsAdapter(import.meta.env, contractorId, scope.siteId);
      let page = await adapter.loadWorkspace();
      const total = page.totalCount;
      const records = [...page.records];
      const cursors = new Set<string>();
      while(page.nextCursor && cursors.size < 20 && !cursors.has(page.nextCursor)) {
        cursors.add(page.nextCursor);
        page = await adapter.loadWorkspace(page.nextCursor);
        records.push(...page.records);
      }
      // The server count covers the entire authorized ledger, not just this page.
      const { summarizeLegalFlag } = await import('../chemical-msds/model');
      return { total, incomplete: page.nextCursor ? null : records.filter(record =>
        ['managed','special','measurement','healthExam','permit','specialEducation'].some(key =>
          summarizeLegalFlag(record, key as Parameters<typeof summarizeLegalFlag>[1]) === 'review')).length };
    }),
    safe('safety-cost', async () => {
      const { createBudgetOpexRepository } = await import('../budget-materials/budget-opex-repository');
      const repository = createBudgetOpexRepository(contractorId, scope.siteId);
      if (!repository) throw Error('BUDGET_UNAVAILABLE');
      const data = await repository.load({ from: month, to: month, owner: contractorId ? 'contractor' : 'all', company: '', category: '', search: '', offset: 0 });
      if (!data.complete || data.heldCount) throw Error('BUDGET_INCOMPLETE');
      return { amount: data.aggregates.principal + data.aggregates.contractor, count: data.aggregates.count };
    }),
    safe('meeting-minutes', async () => {
      const { createMeetingMinutesRepository } = await import('../communications/meeting-minutes-repository');
      const to = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 0)).toISOString().slice(0, 10);
      const page = await createMeetingMinutesRepository(scope.siteId).list({ search: '', dateFrom: `${month}-01`, dateTo: to, kind: '', cadence: '', cursor: null });
      return { total: page.totalCount };
    }),
  ]);
  return { msds, budget, meetings };
}
